import React, { useEffect, useState } from "react";
import Spinner from "../components/Spinner";
import TableWrapper from "../components/TableWrapper";

export default function MatchOdds({ fixtureId }) {
  const [oddsData, setOddsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!fixtureId) return;
    let mounted = true;
    const fetchOdds = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/odds/get?fixtureId=${fixtureId}&bookmakerId=8`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              key: process.env.REACT_APP_ACCESKEY,
            },
          },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (mounted) setOddsData(json);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load odds.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchOdds();
    return () => {
      mounted = false;
    };
  }, [fixtureId]);

  const filterOddsBySearch = (bet) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase().trim();

    // Caută în numele bet-ului (dacă există și e string)
    if (bet.name && typeof bet.name === "string") {
      if (bet.name.toLowerCase().includes(term)) return true;
    }

    // Caută în valorile bet-ului
    return (bet.values || []).some((v) => {
      // Verifică dacă v.value există și îl convertește în string
      if (v.value !== null && v.value !== undefined) {
        const valueStr = String(v.value).toLowerCase();
        return valueStr.includes(term);
      }
      return false;
    });
  };

  if (loading) return <Spinner text="Loading odds..." />;
  if (error)
    return (
      <div className="p-4 bg-red-950/30 text-red-400 rounded-lg border border-red-900/50 text-sm">
        {error}
      </div>
    );
  if (!oddsData?.response?.[0])
    return (
      <div className="text-gray-500 p-4 text-sm bg-gray-800/30 rounded-lg border border-gray-700/50">
        No odds available.
      </div>
    );

  const bookmaker = oddsData.response[0].bookmakers?.[0];
  if (!bookmaker)
    return (
      <div className="text-gray-500 p-4 text-sm bg-gray-800/30 rounded-lg border border-gray-700/50">
        No bookmaker odds available.
      </div>
    );

  const filteredBets = (bookmaker.bets || []).filter(filterOddsBySearch);

  const tableHeaderClass =
    "text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-700";
  const tableCellClass =
    "py-3 px-4 text-sm text-gray-400 border-b border-gray-800";
  const tableCellCenterClass =
    "py-3 px-4 text-sm text-gray-400 text-center border-b border-gray-800";

  return (
    <div className="mt-4">
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search odds by market or value..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 bg-gray-800/50 border border-gray-700 rounded-lg text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-500/50 transition-colors"
          />
          <svg
            className="absolute left-3 top-2.5 h-4 w-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="mt-2 text-xs text-gray-500">
            Found{" "}
            {filteredBets.reduce(
              (acc, bet) => acc + (bet.values?.length || 0),
              0,
            )}{" "}
            odds in {filteredBets.length} markets
          </div>
        )}
      </div>

      {/* Desktop View - Hidden on mobile */}
      <div className="hidden md:block">
        {console.log(filteredBets)}
        <TableWrapper titleColor="text-green-400" title="Betting Odds">
          {filteredBets.length === 0 ? (
            <div className="text-gray-500 p-4 text-center bg-gray-800/30 rounded-lg border border-gray-700/50">
              No odds match your search.
            </div>
          ) : (
            filteredBets.map((bet) => (
              <div key={bet.id} className="mb-6 last:mb-0">
                <h4 className="text-sm font-medium text-gray-300 mb-3">
                  {bet.name}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className={tableHeaderClass}>Value</th>
                        <th className={tableHeaderClass + " text-right"}>
                          Odd
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bet.values || []).map((v, i) => (
                        <tr
                          key={i}
                          className="hover:bg-gray-700/30 transition-colors"
                        >
                          <td className={tableCellClass}>
                            <div className="truncate">{v.value}</div>
                          </td>
                          <td
                            className={`${tableCellCenterClass} text-right font-medium text-green-500`}
                          >
                            {v.odd}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </TableWrapper>
      </div>

      {/* Mobile View - Visible only on mobile */}
      <div className="md:hidden space-y-4">
        <TableWrapper titleColor="text-green-400" title="Betting Odds">
          {filteredBets.length === 0 ? (
            <div className="text-gray-500 p-4 text-center bg-gray-800/30 rounded-lg border border-gray-700/50">
              No odds match your search.
            </div>
          ) : (
            filteredBets.map((bet) => (
              <div key={bet.id} className="mb-4 last:mb-0">
                <h4 className="text-sm font-medium text-gray-300 mb-2">
                  {bet.name}
                </h4>
                <div className="space-y-2">
                  {(bet.values || []).map((v, i) => (
                    <div
                      key={i}
                      className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/50 flex justify-between items-center"
                    >
                      <span className="text-sm text-gray-300 flex-1 pr-2">
                        {v.value}
                      </span>
                      <span className="text-sm font-medium text-green-500 bg-gray-900/50 px-3 py-1 rounded whitespace-nowrap">
                        {v.odd}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </TableWrapper>
      </div>
    </div>
  );
}
